import { useCallback, useMemo } from 'react';
import { useBatchId } from './useBatchId';
import { NavigateOptions } from 'react-router-dom';
import { useToastMessage } from './useToastMessage';
import { useCallApi } from './useCallApi';
import { getApplication } from '../service/VolunteerApplicationService';
import { useAuth } from './useAuth';
import { useTranslation } from 'react-i18next';
import useUserSession from './useUserSession';

export const useApplicationId = () => {
	const { navigateToRoute, queryParams } = useBatchId();
	const { showToastMessage } = useToastMessage();
	const { callApi } = useCallApi();
	const { srcmId } = useAuth();
	const { selectedRole } = useUserSession();
	const { i18n } = useTranslation();

	const applicationId = useMemo(() => {
		const applicationIdParam = Number(queryParams.get('applicationId'));

		if (typeof applicationIdParam !== 'number') {
			return -1;
		}

		return applicationIdParam;
	}, [queryParams]);

	const navigateWithApplicationId = useCallback(
		(route: string, applicationIdParam?: number, params?: NavigateOptions) => {
			const applicationIdToUse = applicationIdParam
				? `${applicationIdParam}`
				: applicationId;
			queryParams.set('applicationId', `${applicationIdToUse}`);
			navigateToRoute(route, params);
		},
		[applicationId, navigateToRoute, queryParams]
	);

	const fetchApplication = useCallback(async () => {
		if (!applicationId) {
			showToastMessage('error', 'applicationId is missing');
			throw Error('ApplicationId is missing while fetching application');
		}

		if (!selectedRole) {
			showToastMessage('error', 'role is missing');
			throw Error('Role is missing. Please login again.');
		}

		const response = await callApi(
			() =>
				getApplication({
					applicationId,
					functionarySrcmId: srcmId,
					predefinedReasonLanguage: i18n.language,
					selectedRoleCode: selectedRole,
				}),
			'errorOccurredWhileLoading'
		);

		return response;
	}, [
		applicationId,
		callApi,
		showToastMessage,
		srcmId,
		i18n.language,
		selectedRole,
	]);

	return {
		applicationId,
		navigateWithApplicationId,
		fetchApplication,
	};
};
